import React, { useEffect, useMemo, useState } from 'react';
import { Label, Select } from '@/components/ui';
import { Form, FormField } from '@/components/business';
import { Widget } from './Widget';

const DECIMAL_OPTIONS = ['0', '1', '2', '3'].map(decimal => ({ label: decimal, value: decimal }));

export function NewValueWidgetData ({ form }) {
    const data = form.watch();
    const [site, setSite] = useState('');

    const periodOptions = useMemo(() => [
        { label: i18next.t('glossary.last_value').capitalize(), value: 'last_value' },
        { label: i18next.t('periods.verbose.wtd').capitalize(), value: 'wtd' },
        { label: i18next.t('periods.verbose.mtd').capitalize(), value: 'mtd' },
        { label: i18next.t('periods.verbose.ytd').capitalize(), value: 'ytd' }
    ], []);

    const unitScaleOptions = useMemo(() => [
        { label: i18next.t('unit_scales.auto'), value: -1 },
        { label: i18next.t('unit_scales.full'), value: 0 },
        { label: i18next.t('unit_scales.thousands'), value: 3 },
        { label: i18next.t('unit_scales.millions'), value: 6 },
        { label: i18next.t('unit_scales.billions'), value: 9 }
    ], []);

    const sitesOptions = useMemo(
        () => WiseMetering.zones.activeSites().map(model => ({ label: model.get('name'), value: model.id })),
        []
    );

    const indicatorOptions = useMemo(
        () => WiseMetering.indicators
            .filter(model => model.get('site_id') === site)
            .map(model => ({ label: model.get('name'), value: model.id })),
        [site]
    );

    // When the site changes, clear the indicator selection.
    useEffect(() => {
        form.setValue('data.indicatorId', '');
    }, [site, form]);

    return (
        <div className="tw-flex tw-gap-6">
            <div className="tw-flex-1">
                <Form form={form}>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <Label>{i18next.t('glossary.site').capitalize()}</Label>
                        <Select
                            onChange={selectedOption => {
                                const value = selectedOption?.value ?? '';
                                setSite(value);

                                const selected = sitesOptions.find(i => i.value === value);
                                form.setValue('subtitle', selected?.label || '');
                            }}
                            options={sitesOptions}
                            placeholder={i18next.t('sites.select_site')}
                            sort="asc"
                            value={sitesOptions.find(option => option.value === site) ?? null}
                        />
                    </div>
                    <FormField name="data.indicatorId" label={i18next.t('glossary.indicator').capitalize()}
                        render={({ field }) => (
                            <Select
                                onChange={selectedOption => {
                                    const value = selectedOption?.value ?? '';
                                    field.onChange(value);

                                    const selected = indicatorOptions.find(i => i.value === value);
                                    form.setValue('title', selected?.label || '');
                                }}
                                options={indicatorOptions}
                                placeholder={i18next.t('indicators.select_indicator').capitalize()}
                                sort="asc"
                                value={indicatorOptions.find(option => option.value === field.value) ?? null}
                            />
                        )}
                    />
                    <FormField name="data.period" label={i18next.t('common.reference_period').capitalize()}
                        render={({ field }) => (
                            <Select
                                onChange={selectedOption => {
                                    const value = selectedOption?.value ?? '';
                                    field.onChange(value);

                                    const selected = periodOptions.find(p => p.value === value);
                                    form.setValue('legend', selected?.label || '');
                                }}
                                options={periodOptions}
                                placeholder={i18next.t('common.reference_period').capitalize()}
                                sort={null}
                                value={periodOptions.find(option => option.value === field.value) ?? null}
                            />
                        )}
                    />
                    <FormField name="data.unitScale" label={i18next.t('indicatorKinds.power_factor').capitalize()}
                        render={({ field }) => (
                            <Select
                                onChange={selectedOption => field.onChange(selectedOption?.value ?? -1)}
                                options={unitScaleOptions}
                                placeholder={i18next.t('indicatorKinds.power_factor').capitalize()}
                                sort={null}
                                value={unitScaleOptions.find(option => option.value === field.value) ?? null}
                            />
                        )}
                    />
                    <FormField name="data.decimals" label={i18next.t('glossary.decimals').capitalize()}
                        render={({ field }) => (
                            <Select
                                onChange={selectedOption => field.onChange(selectedOption?.value ?? '')}
                                options={DECIMAL_OPTIONS}
                                placeholder={i18next.t('glossary.decimals').capitalize()}
                                sort={null}
                                value={DECIMAL_OPTIONS.find(option => option.value === field.value) ?? null}
                            />
                        )}
                    />
                </Form>
            </div>
            <div className="tw-w-widget-preview tw-h-widget-preview tw-flex-shrink-0">
                <Widget widget={data}/>
            </div>
        </div>
    );
}
